import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { Category } from "models/Checklist";
import { OrderStatus } from "models/Order";

export type SortBy = "ordernr" | "companyName" | "orderStatus" | "lastStatusUpdate" | "updatedAt";
export type SortDir = "asc" | "desc";

export type OrderFilters = {
  filterByReferenceOrder: boolean | null;
  filterOnlyCustomerFollowUpNotDone?: boolean | null;
  filterByOrderStatus: OrderStatus | null;
  filterNotRatedChecklists: boolean | null;
  filterByHasReferenceContact: boolean | null;
  filterByReferenceContactName: string | null;
  filterBySellerSignature: string | null;
  filterByCompanyCode: string | null;
  filterByRoomCategory: Category | null;
  fromDate: string | null;
  toDate: string | null;
};

type OrdersState = {
  search: string;
  filters: OrderFilters;
  sort: { sortBy: SortBy; sortDir: SortDir };
  lastViewedOrder: string | null;
};

export const DEFAULT_FILTERS: OrderFilters = {
  filterByReferenceOrder: false,
  filterByHasReferenceContact: false,
  filterByReferenceContactName: null,
  filterOnlyCustomerFollowUpNotDone: false,
  filterByOrderStatus: null,
  filterNotRatedChecklists: null,
  filterBySellerSignature: null,
  filterByCompanyCode: null,
  filterByRoomCategory: null,
  fromDate: null,
  toDate: null,
} as const;

export const initialState: OrdersState = {
  search: "",
  filters: DEFAULT_FILTERS,
  sort: { sortBy: "updatedAt", sortDir: "desc" },
  lastViewedOrder: null,
};

export const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setFilters: (state, action: PayloadAction<OrderFilters>) => {
      state.filters = action.payload;
    },
    setSort: (state, action: PayloadAction<{ sortBy: SortBy; sortDir: SortDir }>) => {
      state.sort = action.payload;
    },
    setLastViewedOrder: (state, action: PayloadAction<string | null>) => {
      state.lastViewedOrder = action.payload;
    },
  },
});

export const selectSearch = (state: RootState) => state.orders.search;
export const selectFilters = (state: RootState) => state.orders.filters;
export const selectSort = (state: RootState) => state.orders.sort;
export const selectLastViewedOrder = (state: RootState) => state.orders.lastViewedOrder;

export const { setSearch, setFilters, setSort, setLastViewedOrder } = ordersSlice.actions;

export default ordersSlice.reducer;
